/**
 * Admin route constants — single source of truth for every navigable
 * URL path in the panel.
 *
 * CLAUDE.md absolute rule #12 (Routes constants): no literal route
 * strings in `navigate(...)`, `<Link to=...>`, `<Navigate to=...>` or
 * `<Route path=...>` calls. A renamed path becomes a one-line change;
 * a typo becomes a typecheck failure rather than a 404 at runtime.
 *
 * v1 + AUT-110 surfaces:
 *   - `Auth.Login` — email+password entry (AUT-110 rewrites the body)
 *   - `Auth.LoginTotp` — reserved deep-link to the TOTP step
 *   - `Auth.Accept` — invite-acceptance set-password page (AUT-110)
 *   - `Auth.ResetRequest` — request a password-reset link (AUT-110)
 *   - `Auth.ResetConfirm` — submit new password via reset-token (AUT-110)
 *   - `App.Dashboard` — placeholder skeleton page
 *   - `App.MoneyHealth` — «Нужно внимание»: the four money signals, polled (AUT-328)
 *   - `App.Health` — `GET /health` smoke target
 *   - `App.Orders` — orders moderation list + detail drawer (AUT-123)
 *   - `App.Users` / `UserDetail` — users moderation list + detail drawer (AUT-124); the
 *     drawer lives in the path since AUT-542 — build it with {@link userDetailPath}.
 *   - `App.Inspectors` — inspectors moderation list + detail drawer (AUT-125)
 *   - `App.Pickers` — «Подборщики»: picker-tool users list + drawer, disable / enable (AUT-400)
 *   - `App.Companies` / `CompanyNew` / `CompanyDetail` — «Компании»: list, create, card with
 *     tabs (AUT-403). The detail path carries `:id`; build it with {@link companyDetailPath}.
 *   - `App.VehicleCatalog` / `VehicleCatalogMake` — «Справочник авто»: makes list + make
 *     drawer (AUT-437). The make path carries `:makeId`; build it with {@link vehicleCatalogMakePath}.
 *   - «Площадка» (AUT-542) — `App.Specialists`, `CarListings`, `ContentReports`, `CarRequests`,
 *     each with a `…Detail` path carrying `:id` (the list with that card's drawer open);
 *     build them with the `…DetailPath` functions below.
 *   - `App.Reviews` — «Отзывы» (AUT-542 PR2): no `:id` path — there is no
 *     single-review endpoint, the card opens from a row of the list.
 *   - `App.Reports` — reports reference list + read-only detail drawer (AUT-127; moderation retired by AUT-265)
 *   - `App.Appeals` — appeals queue + detail drawer (review/resolve) (AUT-130)
 *   - `App.Payouts` — payouts triage queue + detail drawer (AUT-128)
 *   - `App.Refunds` — refunds history list (read-only) (AUT-129)
 *   - `App.Cards` — payout-cards monitoring list + force-unbind (AUT-126)
 *   - `App.Config` — runtime-config table (inline edit + history) (AUT-131)
 *   - `NotFound` — catch-all (`*`)
 *
 * NB: admin routes carry NO `/admin` prefix — the panel is served from
 * its own origin, so `/users` (not `/admin/users`) matches the existing
 * `/dashboard` / `/health` convention.
 */
export const Routes = {
  Auth: {
    Login: '/auth/login',
    LoginTotp: '/auth/totp',
    Accept: '/auth/accept',
    ResetRequest: '/auth/reset',
    ResetConfirm: '/auth/reset/confirm',
  },
  App: {
    Dashboard: '/dashboard',
    MoneyHealth: '/money-health',
    Health: '/health',
    Orders: '/orders',
    Users: '/users',
    UserDetail: '/users/:id',
    Inspectors: '/inspectors',
    Pickers: '/pickers',
    Companies: '/companies',
    CompanyNew: '/companies/new',
    CompanyDetail: '/companies/:id',
    VehicleCatalog: '/vehicle-catalog',
    VehicleCatalogMake: '/vehicle-catalog/:makeId',
    Specialists: '/specialists',
    SpecialistDetail: '/specialists/:id',
    CarListings: '/car-listings',
    CarListingDetail: '/car-listings/:id',
    Reviews: '/reviews',
    ContentReports: '/content-reports',
    ContentReportDetail: '/content-reports/:id',
    Chats: '/chats',
    ChatDetail: '/chats/:id',
    CarRequests: '/car-requests',
    CarRequestDetail: '/car-requests/:id',
    Leads: '/leads',
    VitrineCollections: '/vitrine/collections',
    VitrineRbPrices: '/vitrine/rb-prices',
    Reports: '/reports',
    Appeals: '/appeals',
    Payouts: '/payouts',
    Refunds: '/refunds',
    Cards: '/cards',
    Config: '/config',
  },
  NotFound: '*',
} as const;

/** `/companies/:id` for one company (AUT-403). */
export const companyDetailPath = (id: string): string =>
  Routes.App.CompanyDetail.replace(':id', encodeURIComponent(id));

/** `/vehicle-catalog/:makeId` — the list with one make's drawer open (AUT-437). */
export const vehicleCatalogMakePath = (makeId: string): string =>
  Routes.App.VehicleCatalogMake.replace(':makeId', encodeURIComponent(makeId));

/** `/users/:id` — the users list with that user's drawer open (AUT-542, architect B1). */
export const userDetailPath = (id: string): string =>
  Routes.App.UserDetail.replace(':id', encodeURIComponent(id));

/** `/specialists/:id` — «Специалисты» with that profile's drawer open (AUT-542). */
export const specialistDetailPath = (id: string): string =>
  Routes.App.SpecialistDetail.replace(':id', encodeURIComponent(id));

/** `/car-listings/:id` — «Объявления» with that listing's drawer open (AUT-542). */
export const carListingDetailPath = (id: string): string =>
  Routes.App.CarListingDetail.replace(':id', encodeURIComponent(id));

/** `/content-reports/:id` — «Жалобы» with that complaint's drawer open (AUT-542). */
export const contentReportDetailPath = (id: string): string =>
  Routes.App.ContentReportDetail.replace(':id', encodeURIComponent(id));

/** `/chats/:id` — «Чаты» with that chat's drawer open (AUT-1054). */
export const chatDetailPath = (id: string): string =>
  Routes.App.ChatDetail.replace(':id', encodeURIComponent(id));

/** `/car-requests/:id` — «Заявки» with that request's drawer open (AUT-542). */
export const carRequestDetailPath = (id: string): string =>
  Routes.App.CarRequestDetail.replace(':id', encodeURIComponent(id));

export type AuthRoutePath = (typeof Routes.Auth)[keyof typeof Routes.Auth];
export type AppRoutePath = (typeof Routes.App)[keyof typeof Routes.App];
